import React, { useState, useEffect } from 'react';
import './AnalysisFieldEditor.css';

/**
 * Componente genérico de edição inline para campos da análise v4.0.
 * Suporta: text, textarea, select, number, boolean.
 * Exibe badges IA/Humano para rastreabilidade.
 */
function AnalysisFieldEditor({
  label,
  campo,
  valorAtual,
  tipo = 'text',       // 'text' | 'textarea' | 'select' | 'number' | 'boolean'
  opcoes = [],          // Para tipo 'select'
  onSave,              // (campo, novoValor) => void
  editadoPorHumano = false,
  disabled = false,
  placeholder = '',
  rows = 3,
  mostrarBadge = true,  // #51: campos manuais (ex: custo) não exibem selo IA/Humano
  onEditingChange,      // #219 Fase 2: (campo, aberto) — guard de "não salvo" no pai
  ajuda = '',           // Tooltip de ajuda: ícone "?" ao lado do label com title= (aditivo)
}) {
  const [editing, setEditing] = useState(false);
  const [valor, setValor] = useState(valorAtual ?? '');
  const [saving, setSaving] = useState(false);

  // #219 Fase 2 — reporta abrir/fechar do lápis pro pai (NCEditor agrega num
  // Set por campo → o guard de navegação avisa antes de perder texto digitado).
  // Cleanup cobre o unmount com o lápis aberto (ex.: seção colapsada); o pai é
  // idempotente, então o sinal repetido no fechamento normal é inócuo.
  useEffect(() => {
    if (typeof onEditingChange !== 'function') return undefined;
    onEditingChange(campo, editing);
    return () => onEditingChange(campo, false);
  }, [editing, campo, onEditingChange]);

  const handleStartEdit = () => {
    if (disabled) return;
    setValor(valorAtual ?? '');
    setEditing(true);
  };

  const handleCancel = () => {
    setValor(valorAtual ?? '');
    setEditing(false);
  };

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave(campo, valor);
      setEditing(false);
    } catch (err) {
      console.error('Erro ao salvar campo:', err);
    } finally {
      setSaving(false);
    }
  };

  const renderBadge = () => {
    if (!mostrarBadge) return null;  // #51: campo manual não tem origem IA/Humano
    if (editadoPorHumano) {
      return (
        <span className="badge-humano" title="Editado por humano">
          <span className="material-symbols-outlined afe-badge-icon">edit</span>
          Editado
        </span>
      );
    }
    return (
      <span className="badge-ia" title="Gerado pela IA">
        <span className="material-symbols-outlined afe-badge-icon">auto_awesome</span>
        IA
      </span>
    );
  };

  const renderAjuda = () => {
    if (!ajuda) return null;
    return (
      <span
        className="afe-ajuda material-symbols-outlined"
        title={ajuda}
        role="img"
        aria-label={ajuda}
        tabIndex={0}
      >
        help
      </span>
    );
  };

  const renderDisplayValue = () => {
    if (valorAtual === null || valorAtual === undefined || valorAtual === '') {
      return <span className="field-empty">Não preenchido</span>;
    }
    if (tipo === 'boolean') {
      return valorAtual ? 'Sim' : 'Não';
    }
    return String(valorAtual);
  };

  const renderInput = () => {
    switch (tipo) {
      case 'textarea':
        return (
          <textarea
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            placeholder={placeholder}
            rows={rows}
            disabled={saving}
            className="afe-input afe-textarea"
          />
        );
      case 'select':
        return (
          <select
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            disabled={saving}
            className="afe-input afe-select"
          >
            <option value="">Selecione...</option>
            {opcoes.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'number':
        return (
          <input
            type="number"
            value={valor}
            onChange={(e) => setValor(Number(e.target.value))}
            placeholder={placeholder}
            disabled={saving}
            className="afe-input"
          />
        );
      case 'boolean':
        return (
          <select
            value={valor ? 'true' : 'false'}
            onChange={(e) => setValor(e.target.value === 'true')}
            disabled={saving}
            className="afe-input afe-select"
          >
            <option value="true">Sim</option>
            <option value="false">Não</option>
          </select>
        );
      default:
        return (
          <input
            type="text"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            placeholder={placeholder}
            disabled={saving}
            className="afe-input"
          />
        );
    }
  };

  if (editing) {
    return (
      <div className="afe-container afe-editing">
        <div className="afe-label-row">
          <label className="afe-label">{label}</label>
          {renderAjuda()}
          {renderBadge()}
        </div>
        {renderInput()}
        <div className="afe-actions">
          <button
            className="afe-btn afe-btn-save"
            onClick={handleSave}
            disabled={saving}
          >
            <span className="material-symbols-outlined afe-btn-icon">{saving ? 'hourglass_empty' : 'save'}</span>
            Salvar
          </button>
          <button
            className="afe-btn afe-btn-cancel"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="afe-container afe-display">
      <div className="afe-label-row">
        <label className="afe-label">{label}</label>
        {renderAjuda()}
        {renderBadge()}
        {!disabled && (
          <button
            className="afe-btn-edit"
            onClick={handleStartEdit}
            title="Editar campo"
            aria-label="Editar campo"
          >
            <span className="material-symbols-outlined afe-edit-icon">edit</span>
          </button>
        )}
      </div>
      <div className="afe-value">{renderDisplayValue()}</div>
    </div>
  );
}

export default AnalysisFieldEditor;
